import React, { useEffect, Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import ServiceProviderLayout from "../../components/Layout/AppLayout/ServiceProviderLayout";
import UserLayout from "../../components/Layout/AppLayout/UserLayout";
import UsersProtectedRoutes from "./UsersProtectedRoutesComp.js";
import ServiceProvidersProtectedRoutes from "./ServiceProvidersProtectedRouteComp.js";
import { useDispatch, useSelector } from "react-redux";
import { listAndCardStatesClear, selectedFiltersValueClear } from "../../store/common/servicesSlice/index.js";
import FullPageLoader from "../../components/FullPageLoader/index.js";
import Profile from "../../pages/Common/Profile/index.js";
import AddEditPost from "../../pages/Common/Posts/AddEditPost/index.js";
import SharedPost from "../../pages/Common/Posts/Post/PostContent/SharedPost/index.js";
import MyGarage from "../../pages/Common/MyGarage/index.js";
import Bookings from "../../pages/Common/Bookings/index.js";
import BookingDetails from "../../pages/Common/Bookings/MyBookingsList/BookingDetails/index.js";
import GetQuote from "../../pages/User/Quotation/GetQuote/index.js";
import ReceivedQuotes from "../../pages/User/Quotation/ReceivedQuotes/index.js";
import QuoteDetails from "../../pages/Common/Quotation/QuoteDetails/index.js";
import Notifications from "../../pages/Common/Notifications/index.js";
import Settings from "../../pages/Common/Settings/index.js";
import JobDetails from "../../pages/ServiceProvider/Jobs/MyJobsList/JobDetails/index.js";
import ServiceProviderQuotation from "../../pages/ServiceProvider/Quotation/index.js";
import BusinessInfo from "../../pages/Common/Settings/BusinessInfo/index.js";
import EditProfile from "../../pages/Common/Settings/EditProfile/index.js"
import UpdateBusinessInfo from "../../pages/ServiceProvider/Business/UpdateBusinessInfo/index.js"
import VerifyBusiness from "../../pages/Common/Settings/VerifyBusiness/index.js"
import TransactionHistory from "../../pages/Common/Settings/TransactionHistory/index.js";
import Chats from "../../pages/Common/Chats/index.js";
import MyRequestsForProposal from '../../pages/User/Quotation/MyRequestsForProposal/index.js'
import RequestDetails from "../../pages/User/Quotation/MyRequestsForProposal/RequestDetails/index.js";
import Faqs from "../../pages/Common/Settings/Faqs/index.js";
import BenefitsOfMobrage from "../../pages/Common/Settings/BenefitsOfMobrage/index.js";
import CreateAd from "../../pages/Common/Settings/CreateAd/index.js";
import StripeManagement from "../../pages/Common/Settings/ManagePayments/StripeManagement/index.js";
import TransactionDetail from "../../pages/Common/Settings/TransactionHistory/transactionDetail.js";
import Badges from "../../pages/Common/Settings/Badges/index.js";
import ReceivedReviews from "../../pages/ServiceProvider/Reviews/ReceivedReviews/index.js";
import BlockUsers from "../../pages/Common/Settings/BlockUsers/index.js";
import BusinessSections from "../../pages/Common/Settings/BusinessInfo/businessSections.js";
import BusinessDetails from "../../pages/Common/Settings/BusinessInfo/businessDetails.js";
import AddServices from "../../pages/Common/Settings/BusinessInfo/addServices.js";
import AddSchedule from "../../pages/Common/Settings/BusinessInfo/addSchedule.js";
import ServiceListing from "../../pages/Common/Settings/BusinessInfo/serviceListing.js";

// Lazy loaded components
const SpHome = lazy(() => import("../../pages/ServiceProvider/Home/index.js"));
const UserHome = lazy(() => import("../../pages/User/Home/index.js"));
const Posts = lazy(() => import("../../pages/Common/Posts/index.js"));
const LocalGaragesSpCardView = lazy(() => import("../../pages/Common/LocalGarages/SpCardView/index.js"));
const LocalGaragesSpListView = lazy(() => import("../../pages/Common/LocalGarages/SpListView/index.js"));

const AppRoutes = () => {
  const user = useSelector(state => state?.auth?.loggedInUser);

  const location = useLocation();
  const dispatch = useDispatch();

  // A cleanup function to reset the state when paths are not these.
  useEffect(() => {
    const pathsToPersist = [/^\/serviceprovider\/\d+$/, /^\/serviceprovider\/listView\/\d+$/, /^\/profile\/\d+$/];
    const isPathPersisted = pathsToPersist.some((regex) => regex.test(location.pathname));

    if (!isPathPersisted) {
      dispatch(listAndCardStatesClear());
      dispatch(selectedFiltersValueClear());
    }
  }, [location.pathname, dispatch]);

  return (
    <div className="App">
      {user && user?.type ? (
        user.type.toLowerCase().includes('provider') ? (
          <ServiceProviderLayout expandible={false}>
            <Suspense fallback={<FullPageLoader />}>
              <Routes>
                <Route element={<ServiceProvidersProtectedRoutes />}>
                  <Route path="/" element={<SpHome />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/profile/:userId" element={<Profile />} />
                  <Route path="/serviceprovider/:id" element={<LocalGaragesSpCardView />} />
                  <Route path="/serviceprovider/listView/:id" element={<LocalGaragesSpListView />} />
                  <Route path="/posts" element={<Posts />} />
                  <Route path="/posts/:hashtag" element={<Posts />} />
                  <Route path="/post/add" element={<AddEditPost />} />
                  <Route path="/post/:postId" element={<SharedPost />} />
                  <Route path="/post/share/:postId" element={<SharedPost />} />
                  <Route path="/my-garage" element={<MyGarage />} />
                  <Route path="/business/verify" element={<VerifyBusiness />} />
                  <Route path="/booking/my-bookings" element={<Bookings />} />
                  <Route path="/booking/my-bookings/:bookingStatusId" element={<Bookings />} />
                  <Route path="/booking/:bookingId" element={<BookingDetails />} />
                  <Route path="/job/:jobId" element={<JobDetails />} />
                  <Route path="/myjobs/:jobId" element={<JobDetails />} />
                  <Route path="/quotes" element={<ServiceProviderQuotation />} />
                  <Route path="/quote/:quoteId" element={<QuoteDetails />} />
                  <Route path="/quote/:quoteId/price-proposal" element={<QuoteDetails />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/settings" element={<Settings />} />

                  {/* business updated on going work == */}
                  {/* <Route path="/settings/businessInfo" element={<BusinessInfo />} /> */}
                  <Route path="/settings/businessInfo" element={<BusinessSections />} />
                  <Route path="/settings/businessInfo/details" element={<BusinessDetails />} />
                  <Route path="/settings/businessInfo/services" element={<ServiceListing />} />
                  <Route path="/settings/businessInfo/services/:id" element={<AddServices />} />
                  <Route path="/settings/businessInfo/schedule" element={<AddSchedule />} />
                  <Route path="/settings/businessInfo/temp" element={<BusinessInfo />} />

                  <Route path="/settings/editProfile" element={<EditProfile />} />
                  <Route path="/settings/Faqs" element={<Faqs />} />
                  <Route path="/settings/verify" element={<VerifyBusiness />} />
                  <Route path="/settings/transactions" element={<TransactionHistory />} />
                  <Route path="/settings/transaction/:id" element={<TransactionDetail />} />
                  <Route path="/settings/payments" element={<StripeManagement />} />
                  <Route path="/settings/badges" element={<Badges />} />
                  <Route path="/settings/createAd" element={<CreateAd />} />
                  <Route path="/settings/blockList" element={<BlockUsers />} />
                  <Route path="/settings/BenefitsOfMobrage" element={<BenefitsOfMobrage />} />
                  <Route path="/chats" element={<Chats />} />
                  <Route path="/reviews" element={<ReceivedReviews />} />
                  <Route path="/reviews/:spId" element={<ReceivedReviews />} />
                  <Route path="*" element={<Navigate to="/" />} />
                </Route>
              </Routes>
            </Suspense>
          </ServiceProviderLayout>
        ) : (
          <UserLayout expandible={false}>
            <Suspense fallback={<FullPageLoader />}>
              <Routes>
                <Route element={<UsersProtectedRoutes />}>
                  <Route path="/" element={<UserHome />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/profile/:userId" element={<Profile />} />
                  <Route path="/serviceprovider/:id" element={<LocalGaragesSpCardView />} />
                  <Route path="/serviceprovider/listView/:id" element={<LocalGaragesSpListView />} />
                  <Route path="/posts" element={<Posts />} />
                  <Route path="/posts/:hashtag" element={<Posts />} />
                  <Route path="/post/add" element={<AddEditPost />} />
                  <Route path="/post/:postId" element={<SharedPost />} />
                  <Route path="/post/share/:postId" element={<SharedPost />} />
                  <Route path="/my-garage" element={<MyGarage />} />
                  <Route path="/business/update" element={<UpdateBusinessInfo />} />
                  <Route path="/booking/my-bookings" element={<Bookings />} />
                  <Route path="/booking/my-bookings/:bookingStatusId" element={<Bookings />} />
                  <Route path="/booking/:bookingId" element={<BookingDetails />} />
                  <Route path="/quote-form" element={<GetQuote />} />
                  <Route path="/quote/:quoteId" element={<QuoteDetails />} />
                  <Route path="/requests" element={<MyRequestsForProposal />} />
                  <Route path="/request/:requestId/quotes" element={<ReceivedQuotes />} />
                  <Route path="/request/:requestId" element={<RequestDetails />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/settings/editProfile" element={<EditProfile />} />
                  <Route path="/settings/Faqs" element={<Faqs />} />
                  <Route path="/settings/transactions" element={<TransactionHistory />} />
                  <Route path="/settings/transaction/:id" element={<TransactionDetail />} />
                  <Route path="/settings/payments" element={<StripeManagement />} />
                  <Route path="/settings/blockList" element={<BlockUsers />} />
                  <Route path="/chats" element={<Chats />} />
                  <Route path="/reviews/:spId" element={<ReceivedReviews />} />
                  <Route path="*" element={<Navigate to="/" />} />
                </Route>
              </Routes>
            </Suspense>
          </UserLayout>
        )
      ) : null
      }
    </div>
  );
};

export default AppRoutes;
